"use client";

import { useId } from 'react';
import { useFormContext } from 'react-hook-form';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { cn } from '@/lib/utils';

interface CheckboxFieldProps {
  name: string;
  label: string;
  description?: string;
  required?: boolean;
  disabled?: boolean;
  appearance?: 'card' | 'field';
}

export function CheckboxField({ 
  name, 
  label, 
  description,
  required = false,
  disabled = false,
  appearance = 'card',
}: CheckboxFieldProps) {
  const form = useFormContext();
  const checkboxId = useId();
  
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const checkbox = (
          <FormControl>
            <Checkbox
              id={checkboxId}
              aria-label={label}
              checked={field.value || false}
              onCheckedChange={(checked) => {
                field.onChange(Boolean(checked));
              }}
              disabled={disabled}
              className={cn(appearance === 'card' && 'mt-0.5')}
            />
          </FormControl>
        );

        if (appearance === 'field') {
          return (
            <FormItem className="flex flex-col gap-2">
              <FormLabel htmlFor={checkboxId}>
                {label} {required && '*'}
              </FormLabel>
              <label
                htmlFor={checkboxId}
                className={cn(
                  'flex h-9 items-center gap-2 rounded-md border bg-background px-3 text-sm shadow-xs transition-colors',
                  'hover:bg-accent/50 has-[[aria-checked=true]]:border-foreground has-[[aria-checked=true]]:bg-accent',
                  disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                )}
              >
                {checkbox}
                <span>Sí</span>
              </label>
              <FormMessage />
            </FormItem>
          );
        }

        return (
          <FormItem className="space-y-0">
            <FormLabel
              htmlFor={checkboxId}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
                'hover:bg-accent/50 has-[[aria-checked=true]]:border-foreground has-[[aria-checked=true]]:bg-accent',
                disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              {checkbox}
              <div className="grid gap-1 font-normal">
                <p className="text-sm leading-none font-medium">
                  {label} {required && '*'}
                </p>
                {description ? (
                  <p className="text-sm text-muted-foreground">{description}</p>
                ) : null}
              </div>
            </FormLabel>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
